import apiClient from './client';

const BASE = '/api/v1/organizations';

const organizationAPI = {
  list: async (params = {}) => {
    const response = await apiClient.get(BASE, { params });
    return response.data;
  },

  getById: async (orgId) => {
    const response = await apiClient.get(`${BASE}/${orgId}`);
    return response.data.data;
  },

  create: async (data) => {
    const response = await apiClient.post(BASE, data);
    return response.data.data;
  },

  update: async (orgId, data) => {
    const response = await apiClient.put(`${BASE}/${orgId}`, data);
    return response.data.data;
  },

  uploadLogo: async (orgId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${BASE}/${orgId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  getMembers: async (orgId) => {
    const response = await apiClient.get(`${BASE}/${orgId}/members`);
    return response.data.data;
  },

  addMember: async (orgId, data) => {
    const response = await apiClient.post(`${BASE}/${orgId}/members`, data);
    return response.data.data;
  },

  updateMemberRole: async (orgId, userId, data) => {
    const response = await apiClient.put(`${BASE}/${orgId}/members/${userId}`, data);
    return response.data.data;
  },

  removeMember: async (orgId, userId) => {
    const response = await apiClient.delete(`${BASE}/${orgId}/members/${userId}`);
    return response.data.data;
  },

  getEmailTemplates: async (orgId) => {
    const response = await apiClient.get(`${BASE}/${orgId}/email-templates`);
    return response.data.data;
  },

  getEmailTemplate: async (orgId, templateType) => {
    const response = await apiClient.get(`${BASE}/${orgId}/email-templates/${templateType}`);
    return response.data.data;
  },

  updateEmailTemplate: async (orgId, templateType, data) => {
    const response = await apiClient.put(`${BASE}/${orgId}/email-templates/${templateType}`, data);
    return response.data.data;
  },

  resetEmailTemplate: async (orgId, templateType) => {
    const response = await apiClient.post(`${BASE}/${orgId}/email-templates/${templateType}/reset`);
    return response.data.data;
  },

  getProposalBranding: async (orgId) => {
    const response = await apiClient.get(`${BASE}/${orgId}/proposal-branding`);
    return response.data.data;
  },

  updateProposalBranding: async (orgId, colors) => {
    const response = await apiClient.put(`${BASE}/${orgId}/proposal-branding`, colors);
    return response.data.data;
  },

  uploadProposalLogo: async (orgId, logoType, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `${BASE}/${orgId}/proposal-branding/logo`,
      formData,
      {
        params: { logo_type: logoType },
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
    return response.data.data;
  },

  clearProposalLogo: async (orgId, logoType) => {
    const response = await apiClient.delete(`${BASE}/${orgId}/proposal-branding/logo`, {
      params: { logo_type: logoType },
    });
    return response.data.data;
  },

  getCloseioConfig: async (orgId) => {
    const response = await apiClient.get(`${BASE}/${orgId}/closeio-config`);
    return response.data.data;
  },

  updateCloseioConfig: async (orgId, config) => {
    const response = await apiClient.put(`${BASE}/${orgId}/closeio-config`, config);
    return response.data.data;
  },

  getAttioConfig: async (orgId) => {
    const response = await apiClient.get(`${BASE}/${orgId}/attio-config`);
    return response.data.data;
  },

  updateAttioConfig: async (orgId, config) => {
    const response = await apiClient.put(`${BASE}/${orgId}/attio-config`, config);
    return response.data.data;
  },

  testAttioConnection: async (orgId) => {
    const response = await apiClient.post(`${BASE}/${orgId}/attio-config/test`);
    return response.data.data;
  },

  setupAttio: async (orgId) => {
    const response = await apiClient.post(`${BASE}/${orgId}/attio-config/setup`);
    return response.data.data;
  },

  // Attio email access through the Attio MCP server
  getAttioMcpConnectUrl: async (orgId, returnTo) => {
    const response = await apiClient.get(`${BASE}/${orgId}/attio-mcp/connect`, {
      params: returnTo ? { return_to: returnTo } : {},
    });
    return response.data.data; // { authorize_url }
  },

  getAttioMcpStatus: async (orgId) => {
    const response = await apiClient.get(`${BASE}/${orgId}/attio-mcp/status`);
    return response.data.data; // { attio_enabled, connection, email_sync }
  },

  disconnectAttioMcp: async (orgId) => {
    const response = await apiClient.delete(`${BASE}/${orgId}/attio-mcp`);
    return response.data.data;
  },

  syncAttioEmails: async (orgId, initialLookbackDays) => {
    const body = initialLookbackDays ? { initial_lookback_days: initialLookbackDays } : {};
    const response = await apiClient.post(`${BASE}/${orgId}/attio-emails/sync`, body);
    return response.data.data; // { status: "started", run_id }
  },

  getOrgCrm: async (orgId) => {
    const response = await apiClient.get(`${BASE}/${orgId}/crm`);
    return response.data.data;
  },

  getTldvConfig: async (orgId) => {
    const response = await apiClient.get(`${BASE}/${orgId}/tldv-config`);
    return response.data.data;
  },

  updateTldvConfig: async (orgId, config) => {
    const response = await apiClient.put(`${BASE}/${orgId}/tldv-config`, config);
    return response.data.data;
  },

  getRecallConfig: async (orgId) => {
    const response = await apiClient.get(`${BASE}/${orgId}/recall-config`);
    return response.data.data;
  },

  updateRecallConfig: async (orgId, config) => {
    const response = await apiClient.put(`${BASE}/${orgId}/recall-config`, config);
    return response.data.data;
  },

  sendRecallBot: async (orgId, payload) => {
    const response = await apiClient.post(`${BASE}/${orgId}/recall/send-bot`, payload);
    return response.data.data;
  },

  getGoogleMeetConfig: async (orgId) => {
    const response = await apiClient.get(`${BASE}/${orgId}/google-meet-config`);
    return response.data.data;
  },

  updateGoogleMeetConfig: async (orgId, config) => {
    const response = await apiClient.put(`${BASE}/${orgId}/google-meet-config`, config);
    return response.data.data;
  },

  getGoogleMeetConnectUrl: async (orgId, returnTo) => {
    const response = await apiClient.get(`${BASE}/${orgId}/google-meet/connect`, {
      params: returnTo ? { return_to: returnTo } : {},
    });
    return response.data.data; // { authorize_url }
  },

  disconnectGoogleMeet: async (orgId) => {
    const response = await apiClient.post(`${BASE}/${orgId}/google-meet/disconnect`);
    return response.data.data;
  },
};

export default organizationAPI;
